import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Landmark, HeartHandshake, Stethoscope, Home, Scale, Receipt, Wifi, ScrollText, ArrowUpRight } from "lucide-react";
import { SERVICES, CONTACT, KMD } from "../../data";
import { Reveal } from "./Reveal";
import { PoweredBy } from "./PoweredBy";

const ICONS = {
  retirement: Landmark,
  life: HeartHandshake,
  health: Stethoscope,
  mortgage: Home,
  legal: Scale,
  tax: Receipt,
  wireless: Wifi,
  willtrust: ScrollText,
};

const ACCENTS = {
  life: { c: "#d4af37", glow: "rgba(212,175,55,0.2)" },
  health: { c: "#4d9fe8", glow: "rgba(29,110,199,0.28)" },
  mortgage: { c: "#e8874d", glow: "rgba(214,106,44,0.22)" },
  legal: { c: "#b98bff", glow: "rgba(130,49,212,0.26)" },
  retirement: { c: "#4dd6a5", glow: "rgba(23,164,117,0.22)" },
  tax: { c: "#e86a6a", glow: "rgba(199,62,62,0.2)" },
  wireless: { c: "#00A8E0", glow: "rgba(0,168,224,0.24)" },
  willtrust: { c: "#e0c06a", glow: "rgba(224,192,106,0.22)" },
};

const ServiceCard = ({ s, delay }) => {
  const Icon = ICONS[s.id];
  const accent = ACCENTS[s.id] || ACCENTS.life;
  return (
    <Reveal delay={delay}>
      <motion.div
        whileHover={{ scale: 1.01 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        data-testid={`service-card-${s.id}`}
        className="group relative h-full overflow-hidden rounded-2xl border border-[#bf953f]/20 bg-[#0a192f] p-7 md:p-8 hover:border-[#d4af37]/50 transition-colors duration-500 flex flex-col"
      >
        {/* hover accent glow */}
        <div
          className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full opacity-40 blur-[80px] transition-opacity duration-500 group-hover:opacity-100"
          style={{ background: accent.glow }}
        />

        <div className="relative z-10 flex-1 flex flex-col">
          <span
            className="inline-flex h-12 w-12 items-center justify-center rounded-xl"
            style={{ border: `1px solid ${accent.c}55`, color: accent.c, background: `${accent.c}0f` }}
          >
            <Icon size={22} strokeWidth={1.5} />
          </span>

          <h3 className="mt-6 font-serif-display text-2xl md:text-[1.7rem] font-medium text-white leading-snug">{s.title}</h3>
          <p className="mt-1 font-body text-sm italic tracking-wide" style={{ color: accent.c }}>{s.tagline}</p>
          <p className="mt-4 font-body font-light text-sm leading-relaxed text-white/60">{s.body}</p>

          <ul className="mt-5 space-y-1.5">
            {s.points.map((p) => (
              <li key={p} className="flex items-center gap-2 font-body text-[13px] text-white/50">
                <span className="h-1 w-1 shrink-0 rounded-full" style={{ background: accent.c }} />
                {p}
              </li>
            ))}
          </ul>

          {s.legalshield && <PoweredBy className="mt-5" />}
          {s.konnectmd && (
            <Link
              to="/konnectmd"
              data-testid="service-konnectmd-badge"
              className="mt-5 inline-flex items-center gap-2 self-start rounded-full bg-white px-4 py-2 transition-transform duration-300 hover:scale-[1.04]"
            >
              <span className="font-body text-[9px] font-semibold uppercase tracking-[0.18em] text-black/50">Featuring</span>
              <img src={KMD.logo} alt="KonnectMD" className="h-5 w-auto object-contain" />
            </Link>
          )}

          {s.link ? (
            <Link
              to={s.link}
              data-testid={`service-cta-${s.id}`}
              className="mt-auto pt-6 inline-flex items-center gap-1.5 font-body text-sm tracking-wide text-[#fcf6ba]/80 hover:text-[#fcf6ba] transition-colors"
            >
              {s.linkLabel || "Learn more"}
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          ) : (
            <a
              href={CONTACT.phoneHref}
              data-testid={`service-cta-${s.id}`}
              className="mt-auto pt-6 inline-flex items-center gap-1.5 font-body text-sm tracking-wide text-[#fcf6ba]/80 hover:text-[#fcf6ba] transition-colors"
            >
              Get details
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          )}
        </div>
      </motion.div>
    </Reveal>
  );
};

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">Our Protection</p>
        <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-white max-w-3xl">
          Eight pillars. <span className="text-gold-gradient italic">One shield</span> around your family.
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.id} s={s} delay={i * 0.04} />
        ))}
      </div>
    </div>
  </section>
);
